import { AiUsageModel } from '@models/imprvCoins';
import { GoalModel } from 'lib/models/goals';
import { PermissionModel } from './authorization';
import { MediaitemModel } from './mediaitems';
import { NodeModel } from './strategies';
import { hasAsyncAssertion, type BlockAssertionModel } from './tasks';

enum AssignmentTaskSlideType {
  START_SLIDE = 'START_SLIDE',
  END_SLIDE = 'END_SLIDE',
  BLOCK = 'BLOCK'
}

enum AssignmentType {
  ACTION = 'ACTION',
  TASK = 'TASK',
  MESSAGE_REPLY = 'MESSAGE_REPLY',
  MESSAGE_READ = 'MESSAGE_READ'
}

enum AssignmentBlockOutputActionType {
  // block output logs actions
  SCORE = 'SCORE',
  USER_INPUT = 'USER_INPUT',
  FILE_UPLOAD = 'FILE_UPLOAD',
  FEEDBACK = 'FEEDBACK',
  // block output data points actions
  BLOCK_ENTERED = 'BLOCK_ENTERED',
  BLOCK_EXITED = 'BLOCK_EXITED',
  CHECK_CLICKED = 'CHECK_CLICKED',
  CHOICE_CLICKED = 'CHOICE_CLICKED',
  ATTACHMENT_DOWNLOADED = 'ATTACHMENT_DOWNLOADED',
  GAME_CANCELLED = 'GAME_CANCELLED',
  GAME_STARTED = 'GAME_STARTED',
  GAME_STATE_UPDATED = 'GAME_STATE_UPDATED',
  GAME_FINISHED = 'GAME_FINISHED',
  TASK_ENTERED = 'TASK_ENTERED',
  TASK_EXITED = 'TASK_EXITED',
  USER_INPUT_UPDATED = 'USER_INPUT_UPDATED',
  VIDEO_PLAYED = 'VIDEO_PLAYED',
  VIDEO_PAUSED = 'VIDEO_PAUSED',
  VIDEO_REWINDED = 'VIDEO_REWINDED',
  VIDEO_STREAMED = 'VIDEO_STREAMED',
  VIDEO_FINISHED = 'VIDEO_FINISHED',
  VIDEO_VOLUME_CHANGED = 'VIDEO_VOLUME_CHANGED',
  VIDEO_QUALITY_CHANGED = 'VIDEO_QUALITY_CHANGED',
  VIDEO_FULLSCREEN_CHANGED = 'VIDEO_FULLSCREEN_CHANGED'
}

interface GetAssignmentsParamsModel {
  dayLte?: Date;
  dayGt?: Date;
  totalMinutes?: number;
  type?: AssignmentType;
  goalSubscriptionId?: number;
  taskEntityId?: string;
}

interface DeprecatedAssignmentModel {
  userId: number;
  goalEntityId: string;
  goalSubscriptionId: number;
  nodeEntityId?: string | null;
}

interface AssignmentModel {
  userId: number;
  goalSubscriptionId: number;
  nodeEntityId?: string | null;
  blockOutputResults: AssignmentBlockOutputResultModel[] | null;

  node?: NodeModel | null;
  goal?: GoalModel | null;
  __permissions?: PermissionModel[];
}

interface AssignmentsResponseModel {
  assignments: AssignmentModel[];
}

interface AssignmentBlockOutputLogModel {
  id: string;
  blockEntityId?: string;
  blockVersionId?: string;
  userId: number;
  createdAt: string;
  actionType: AssignmentBlockOutputActionType;
  value?: Record<string, any> | null;
  timestamp: string;
  aiUsage?: AiUsageModel | null;
}

interface AssignmentBlockOutputCreateModel {
  blockEntityId?: string;
  blockVersionId?: string;
  value: Record<string, any>;
  actionType: AssignmentBlockOutputActionType;
  timestamp: string;
}

interface AssignmentBlockOutputUserInputModel {
  type: string;
  value:
    | boolean
    | number
    | string
    | string[]
    | number[]
    | Date
    | Date[]
    | Record<string, any>
    | Record<string, any>[];
}

interface UploadedFileModel {
  // Here the snake case convention is intended
  // as it represents the raw value from block output logs
  file_upload_id: string;
  mediaitem_id: number;
  original_filename: string;
  content_type: string;
}

interface BlockOutputFeedbackDataModel {
  type: BlockOutputFeedbackType;
  feedbackId: string;
  reviewedBlockOutputId: string;
  // Always present (empty array when the feedback type doesn't carry payload of this kind).
  blockOutputMediaitemFeedbacks: MediaitemFeedbackModel[];
  blockOutputAgentFeedbacks: AgentFeedbackModel[];
}

enum FlashcardCardStatus {
  PENDING = 'PENDING',
  GRADED = 'GRADED',
  FAILED = 'FAILED'
}

interface FlashcardCardModel {
  itemCid: string;
  status: FlashcardCardStatus;
  score: number | null;
  answer: { value: string | number | boolean | null };
  feedback: BlockOutputFeedbackDataModel | null;
}

interface FlashcardAttemptModel {
  attemptId: string;
  finishedAt: string | null;
  isFinished: boolean;
  score: number | null;
  aiUsage: AiUsageModel | null;
  cards: FlashcardCardModel[];
}

interface AssignmentBlockOutputResultModel {
  blockEntityId: string;
  blockVersionId?: string | null;
  startedAt?: Date | null;
  finishedAt?: Date | null;
  duration?: number | null;
  userInput?: AssignmentBlockOutputUserInputModel | null;
  score?: number | null;
  behavioralData?: Array<Record<string, any>> | null;
  sensorsData?: Record<string, any> | null;
  otherData?: Record<string, any> | null;
  uploadedFiles?: UploadedFileModel[] | null;
  feedback?: BlockOutputFeedbackDataModel | null;
  blockOutputLogs?: AssignmentBlockOutputLogModel[] | null;
  uploadedMediaitems?: MediaitemModel[] | null;
  flashcardAttempts?: FlashcardAttemptModel[] | null;
}

interface BlockOutputResultAsMentorItem {
  userId: number;
  goalSubscriptionId: number;
  blockOutputResults: Array<{
    blockEntityId: string;
    blockVersionId?: string | null;
    uploadedMediaitems?: MediaitemModel[] | null;
    userInput?: AssignmentBlockOutputUserInputModel | null;
    feedback?: BlockOutputFeedbackDataModel | null;
  }>;
}

interface AssignmentBlockUserInputModel {
  taskEntityId: string;
  blockEntityId: string;
  blockVersionId: string;
  value: AssignmentBlockOutputUserInputModel;
  timestamp: string;
  mode?: EvaluationMode;
}

interface EvaluateBlockInputResponse {
  score: number | null;
  userInput: AssignmentBlockOutputUserInputModel;
  feedback?: BlockOutputFeedbackDataModel | null;
}

enum UserInputType {
  TEXT = 'TEXT',
  NUMBER = 'NUMBER',
  DATE = 'DATE',
  TIME = 'TIME',
  DATETIME = 'DATETIME',
  BOOLEAN = 'BOOLEAN',
  NUMBER_CHOICE = 'NUMBER_CHOICE',
  ONE_TO_MANY_MAPPING = 'ONE_TO_MANY_MAPPING',
  FILE_UPLOAD = 'FILE_UPLOAD',
  FILE_UPLOAD_CONFIRMATION = 'FILE_UPLOAD_CONFIRMATION',
  FLASHCARD_ANSWER = 'FLASHCARD_ANSWER'
}

enum EvaluationMode {
  SYNC = 'SYNC',
  ASYNC = 'ASYNC'
}

const getEvaluationMode = (assertions: BlockAssertionModel[] | undefined): EvaluationMode =>
  hasAsyncAssertion(assertions) ? EvaluationMode.ASYNC : EvaluationMode.SYNC;

// A reload empties the typed answer, so a failed answer falls back to the one the server returned.
const getTextAnswerToRetry = (
  typedAnswer: unknown,
  currentResult: AssignmentBlockOutputResultModel | null | undefined
): unknown => typedAnswer || currentResult?.userInput?.value;

enum ExplicitBlockOutputType {
  TEXT = 'TEXT',
  NUMBER = 'NUMBER',
  DATE = 'DATE',
  TIME = 'TIME',
  DATETIME = 'DATETIME',
  BOOLEAN = 'BOOLEAN',
  NUMBER_CHOICE = 'NUMBER_CHOICE',
  ONE_TO_MANY_MAPPING = 'ONE_TO_MANY_MAPPING',
  FILE_UPLOAD_CONFIRMATION = 'FILE_UPLOAD_CONFIRMATION'
}

enum FeedbackRequirementType {
  OPTIONAL = 'OPTIONAL',
  REQUIRED = 'REQUIRED'
}

enum BlockOutputFeedbackType {
  FILE_UPLOAD_AI_FEEDBACK_REQUESTED = 'FILE_UPLOAD_AI_FEEDBACK_REQUESTED',
  FILE_UPLOAD_AI_FEEDBACK_READY = 'FILE_UPLOAD_AI_FEEDBACK_READY',
  FILE_UPLOAD_AI_FEEDBACK_FAILED = 'FILE_UPLOAD_AI_FEEDBACK_FAILED',
  FILE_UPLOAD_HUMAN_FEEDBACK_READY = 'FILE_UPLOAD_HUMAN_FEEDBACK_READY',
  FILE_UPLOAD_HUMAN_FEEDBACK_APPROVED = 'FILE_UPLOAD_HUMAN_FEEDBACK_APPROVED',
  CRITERIA_FEEDBACK_REQUESTED = 'CRITERIA_FEEDBACK_REQUESTED',
  CRITERIA_FEEDBACK_READY = 'CRITERIA_FEEDBACK_READY',
  CRITERIA_FEEDBACK_FAILED = 'CRITERIA_FEEDBACK_FAILED'
}

enum AgentFeedbackType {
  POSITIVE = 'POSITIVE',
  // WARNING stays because old feedback logs still use it
  WARNING = 'WARNING',
  IMPROVEMENT = 'IMPROVEMENT',
  INFO = 'INFO'
}

enum UIFeedbackStatus {
  EVALUATING = 'EVALUATING',
  EVALUATED = 'EVALUATED',
  FAILED = 'FAILED'
}

interface AgentFeedbackModel {
  type: AgentFeedbackType;
  text: string;
}

interface TaskAssignmentCreateModel {
  node_entity_id: string;
  goal_subscription_id: number;
}

interface FeedbackEntryModel {
  feedbackId: string;
  feedbackText: string;
  isPassed: boolean;
  relatedCriteriaAssessments?: Array<{
    criteriaId: string;
    requirementType: FeedbackRequirementType;
    justification: string;
    score: number;
    isPassed: boolean;
    shortDescription: string;
  }>;
}

interface MediaitemFeedbackModel {
  mediaitemId?: number;
  mediaitem_id?: number;
  entries: FeedbackEntryModel[];
}

interface BlockOutputFeedbackModel {
  feedback: MediaitemFeedbackModel[];
}

interface FileUploadFeedbackModel {
  hasValidFeedback: boolean;
  isFailed: boolean;
  feedbackData?: MediaitemFeedbackModel[];
  score?: number;
  isFeedbackOutdated: boolean;
}

export {
  AgentFeedbackType,
  AssignmentBlockOutputActionType,
  AssignmentTaskSlideType,
  AssignmentType,
  BlockOutputFeedbackType,
  EvaluationMode,
  ExplicitBlockOutputType,
  FeedbackRequirementType,
  FlashcardCardStatus,
  getEvaluationMode,
  getTextAnswerToRetry,
  UIFeedbackStatus,
  UserInputType
};
export type {
  AgentFeedbackModel,
  AssignmentBlockOutputCreateModel,
  AssignmentBlockOutputLogModel,
  AssignmentBlockOutputResultModel,
  AssignmentBlockOutputUserInputModel,
  AssignmentBlockUserInputModel,
  AssignmentModel,
  AssignmentsResponseModel,
  BlockOutputFeedbackDataModel,
  BlockOutputFeedbackModel,
  BlockOutputResultAsMentorItem,
  DeprecatedAssignmentModel,
  EvaluateBlockInputResponse,
  FeedbackEntryModel,
  FileUploadFeedbackModel,
  FlashcardAttemptModel,
  FlashcardCardModel,
  GetAssignmentsParamsModel,
  MediaitemFeedbackModel,
  TaskAssignmentCreateModel,
  UploadedFileModel
};
